import {
    CrossIcon,
    Edit,
    Eye,
    Filter,
    MoreVertical,
    Plus,
    Search,
    UserCheck,
    UserX,
    X,
    Trash2
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { api, CreateUserRequest, UpdateUserRequest, User, userApi } from '../api';
import { useAuth } from '../hooks/useAuth';
import ModalPortal from './ModalPortal';

interface MobilisationPartner {
    mp_id: number;
    name: string;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
    gst_number?: string;
    status: string;
    metadata?: any;
    created_at: string;
    _count?: {
        centers: number;
        trainers: number;
        mobilisations: number;
        users: number;
    };
}

// Simple validators
const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
};

const validatePhone = (phone: string) => {
    // Simple 10-digit validation
    const regex = /^\d{10}$/;
    return regex.test(phone);
};

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [mps, setMobalisationParterns] = useState<MobilisationPartner[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [mpFilter, setMpFilter] = useState<number | ''>('');
    const [statusFilter, setStatusFilter] = useState<string | ''>('Active');
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingUser, setViewingUser] = useState<User | null>(null);
    const [formData, setFormData] = useState<CreateUserRequest>({
        full_name: '',
        email: '',
        role: '',
        phone: '',
        mp_id: undefined
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const { user: currentUser } = useAuth();
    const [formErrors, setFormErrors] = useState({
        email: '',
        phone: '',
        full_name: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const MP_INDEPENDENT_ROLES = new Set([
        "SUPER_ADMIN",
        "SYSTEM_ADMIN",
        "OPERATIONS_ADMIN",
        "CONTENT_ADMIN",
    ]);

    const VALID_ROLES = [
        "SUPER_ADMIN",
        "SYSTEM_ADMIN",
        "CONTENT_ADMIN",
        "OPERATIONS_ADMIN",
        "MOBILISATION_PARTNER_ADMIN",
        "CENTER_ADMIN",
        "PROJECT_MANAGER",
        "TRAINER",
        "TRAINEE",
    ];

    const rolesRestrictedForMPAdmin = ["SUPER_ADMIN", "SYSTEM_ADMIN", "CONTENT_ADMIN", "OPERATIONS_ADMIN"];
    const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';
    const isSystemAdmin = currentUser?.role === 'SYSTEM_ADMIN';
    const isMobilisationPartnerAdmin = currentUser?.role === 'MOBILISATION_PARTNER_ADMIN';

    const shouldShowMpSelectForRole = (role: string) =>
        (isSystemAdmin || isSuperAdmin) &&
        role !== "TRAINEE" &&
        !MP_INDEPENDENT_ROLES.has(role);

    const rolesForFilterDropdown = isSuperAdmin
        ? VALID_ROLES
        : isSystemAdmin
            ? VALID_ROLES
            : isMobilisationPartnerAdmin
                ? VALID_ROLES.filter(role => !rolesRestrictedForMPAdmin.includes(role))
                : [];

    const rolesForCreateDropdown = isSuperAdmin
        ? VALID_ROLES
        : isSystemAdmin
            ? VALID_ROLES.filter(role => role !== 'SUPER_ADMIN')
            : isMobilisationPartnerAdmin
                ? VALID_ROLES.filter(role => !rolesRestrictedForMPAdmin.includes(role))
                : [];

    const statusForFilterDropdown = ['Active', 'Inactive']

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await userApi.getUsers({
                page: pagination.page,
                limit: pagination.limit,
                search: searchTerm || undefined,
                queryRole: filterRole || undefined,
                queryMpId: mpFilter || undefined,
                queryStatus: statusFilter !='' ? statusFilter : undefined,
            });

            if (response.success && response.data) {
                setUsers(response.data);
                if (response.meta) {
                    setPagination(prev => ({
                        ...prev,
                        total: response.meta!.total,
                        totalPages: response.meta!.totalPages
                    }));
                }
            } else {
                setError(response.message || 'Failed to fetch users');
            }
        } catch (error: any) {
            console.error('Error fetching users:', error);
            setError(error.response?.data?.message || 'Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const fetchMobalisationPartners = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get('/partners');
            if (response.status == 200 && response.data) {
                setMobalisationParterns(response.data.data);
            } else {
                setError(response.statusText || 'Failed to fetch users');
            }
        } catch (error: any) {
            console.error('Error fetching users:', error);
            setError(error.response?.data?.message || 'Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const debounceTimeout = setTimeout(() => {
            fetchUsers();
        }, 300);

        return () => clearTimeout(debounceTimeout);
    }, [pagination.page, pagination.limit, searchTerm, filterRole, mpFilter, statusFilter]);

    useEffect(() => {
        if (currentUser && (currentUser.role === "SYSTEM_ADMIN" || currentUser.role === "SUPER_ADMIN")) {
            fetchMobalisationPartners();
        }
    }, [currentUser]);

    useEffect(() => {
        if (showCreateModal) {
            setFormData(prev => ({
                ...prev,
                mp_id: currentUser?.role === 'MOBILISATION_PARTNER_ADMIN'
                    ? currentUser.mp_id
                    : undefined
            }));
        }
    }, [showCreateModal, currentUser]);


    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = {
            ...formData,
            full_name: formData.full_name.trim(),
            email: formData.email.trim(),
            phone: formData.phone.trim(),
        };
        let errors: any = {};
        if (trimmed.email && !validateEmail(trimmed.email)) {
            errors.email = "Invalid email format";
        }
        if (trimmed.phone && !validatePhone(trimmed.phone)) {
            errors.phone = "Phone must be 10 digits";
        }


        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        try {
            setError(null);
            setIsSubmitting(true);
            if (trimmed.role == "MOBILISATION_PARTNER_ADMIN" && !trimmed.mp_id) {
                setError('Mobilisation Partner is required.');
                return;
            }

            const response = await userApi.createUser(trimmed);

            if (response.success) {
                setSuccess('User created successfully');
                setShowCreateModal(false);
                resetForm();
                fetchUsers();

                // Clear success message after 3 seconds
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(response.message || 'Failed to create user');
            }
        } catch (error: any) {
            console.error('Error creating user:', error);
            setError(error.response?.data?.message || 'Failed to create user');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingUser) return;
        const trimmed = {
            ...formData,
            full_name: formData.full_name.trim(),
            email: formData.email.trim(),
            phone: formData.phone.trim(),
        };
        let errors: any = {};
        if (trimmed.email && !validateEmail(trimmed.email)) {
            errors.email = "Invalid email format";
        }
        if (trimmed.phone && !validatePhone(trimmed.phone)) {
            errors.phone = "Phone must be 10 digits";
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        try {
            setError(null);
            setIsSubmitting(true);

            const updateData: UpdateUserRequest = {
                full_name: trimmed.full_name,
                email: trimmed.email,
                role: trimmed.role,
                phone: trimmed.phone,
                mp_id: trimmed.mp_id ?? undefined
            };

            const response = await userApi.updateUser(editingUser.user_id, updateData);

            if (response.success) {
                setSuccess('User updated successfully');
                setShowEditModal(false);
                setEditingUser(null);
                resetForm();
                fetchUsers();

                // Clear success message after 3 seconds
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(response.message || 'Failed to update user');
            }
        } catch (error: any) {
            console.error('Error updating user:', error);
            setError(error.response?.data?.message || 'Failed to update user');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInActiveUser = async (user: User) => {
        if (!confirm(`Delete ${user.full_name}? This will deactivate their account.`)) return;

        try {
            setLoading(true);
            setError(null);

            const response = await userApi.inActiveUser(user.user_id);

            if (response.success) {
                setSuccess('User deleted (soft) successfully');
                fetchUsers();

                // Clear success message after 3 seconds
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(response.message || 'Failed to delete user. Only Super Admins can deactivate another Super Admin.');
            }
        } catch (error: any) {
            console.error('Error while doing inactive user:', error);
            setError(
                error.response?.data?.message
                || (error.response?.status === 403
                    ? 'Only Super Admins can deactivate another Super Admin.'
                    : 'Failed to delete user'
                )
            );
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (user: User) => {
        setError(null);
        setFormErrors({ email: '', phone: '', full_name: '' });
        setIsSubmitting(false);
        setEditingUser(user);
        setFormData({
            full_name: user.full_name,
            email: user.email,
            role: user.role,
            phone: user.phone || '',
            mp_id: MP_INDEPENDENT_ROLES.has(user.role) ? undefined : user.mp_id
        });
        setShowEditModal(true);
    };
    const openViewModal = (user: User) => {
        setViewingUser(user);
        setShowViewModal(true);
    };


    const resetForm = () => {
        setError(null);
        setFormErrors({ email: '', phone: '', full_name: '' });
        setIsSubmitting(false);
        setFormData({
            full_name: '',
            email: '',
            role: '',
            phone: '',
            mp_id: currentUser?.role === 'MOBILISATION_PARTNER_ADMIN'
                ? currentUser.mp_id
                : undefined,
        });
        setEditingUser(null);
    };

    const getRoleBadgeColor = (role: string) => {
        switch (role) {
            case 'admin':
                return 'bg-red-100 text-red-800';
            case 'manager':
                return 'bg-purple-100 text-purple-800';
            case 'trainer':
                return 'bg-blue-100 text-blue-800';
            case 'coordinator':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatRoleForDisplay = (role: string) => {
        if (!role) return '';
        return role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    };


    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    const isFormModalOpen = showCreateModal || showEditModal;

    return (
        // className="space-y-6"
        <div >
            {/* Notifications */}
            {error && !isFormModalOpen && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{error}</span>
                    <button
                        className="absolute top-0 bottom-0 right-0 px-4 py-3"
                        onClick={() => setError(null)}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{success}</span>
                    <button
                        className="absolute top-0 bottom-0 right-0 px-4 py-3"
                        onClick={() => setSuccess(null)}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Header */}
            <div className="sm:flex sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Manage system users
                    </p>
                </div>
                <div className="mt-4 sm:mt-0">
                    <button
                        onClick={() => {
                            setError(null);
                            setFormErrors({ email: '', phone: '', full_name: '' });
                            setShowCreateModal(true);
                        }}
                        className="btn-primary"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add User
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white shadow rounded-lg p-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                        <label htmlFor="search" className="form-label">
                            Search Users
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                id="search"
                                placeholder="Search by name or email..."
                                className="form-input pl-10"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {(isSystemAdmin || isSuperAdmin) && (
                        <div>
                            <label htmlFor="mp-filter" className="form-label">Filter by Mobilisation Partner</label>
                            <div className="relative">
                                {/* Optional: Add an icon here */}
                                <select
                                    id="mp-filter"
                                    className="form-input"
                                    value={mpFilter}
                                    onChange={e => setMpFilter(e.target.value ? Number(e.target.value) : '')}
                                >
                                    <option value="">All Partners</option>
                                    {mps.map(mp => (
                                        <option key={mp.mp_id} value={mp.mp_id}>
                                            {mp.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}


                    <div>
                        <label htmlFor="role-filter" className="form-label">
                            Filter by Role
                        </label>
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <select
                                id="role-filter"
                                className="form-input pl-10"
                                value={filterRole}
                                onChange={(e) => setFilterRole(e.target.value)}
                            >
                                <option value="">All Roles</option>
                                {rolesForFilterDropdown.map(role => (
                                    <option key={role} value={role} >
                                        {formatRoleForDisplay(role)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="status-filter" className="form-label">
                            Filter by Status
                        </label>
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <select
                                id="status-filter"
                                className="form-input pl-10"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                 <option value="">All Roles</option>
                                {statusForFilterDropdown.map(status => (
                                    <option key={status} value={status} >
                                        {status}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {/* 
                    <div className="flex items-end">
                        <button className="btn-secondary w-full">
                            Export Users
                        </button>
                    </div> */}
                </div>
            </div>

            {/* Users Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                        Users ({pagination.total})
                    </h3>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    User
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Role
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Last Login
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {users.map((user) => (
                                <tr key={user.user_id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                                                <span className="text-sm font-medium text-gray-700">
                                                    {user.full_name.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="ml-3">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {user.full_name}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {user.email}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                                            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.is_active
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                            }`}>
                                            {user.is_active ? (
                                                <>
                                                    <UserCheck className="h-3 w-3 mr-1" />
                                                    Active
                                                </>
                                            ) : (
                                                <>
                                                    <UserX className="h-3 w-3 mr-1" />
                                                    Inactive
                                                </>
                                            )}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {user.last_login ? formatDateTime(user.last_login) : 'Never'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(user.created_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center space-x-2">
                                            <button
                                                className="text-primary-600 hover:text-primary-900"
                                                title="View"
                                                onClick={() => openViewModal(user)}>
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            {user.is_active && <button
                                                className="text-blue-600 hover:text-blue-900"
                                                title="Edit"
                                                onClick={() => openEditModal(user)}
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>}
                                            {user.user_id !== currentUser?.user_id && user.is_active && (
                                                <button
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Delete"
                                                    onClick={() => handleInActiveUser(user)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                            {/* <button className="text-gray-400 hover:text-gray-600" title="More">
                                                <MoreVertical className="h-4 w-4" />
                                            </button> */}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                                disabled={pagination.page === 1}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                                disabled={pagination.page === pagination.totalPages}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
                                    <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of{' '}
                                    <span className="font-medium">{pagination.total}</span> results
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                                        disabled={pagination.page === 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    {/* Page numbers */}
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        const pageNum = Math.max(1, Math.min(pagination.totalPages - 4, pagination.page - 2)) + i;
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pageNum === pagination.page
                                                    ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                                        disabled={pagination.page === pagination.totalPages}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Create User Modal */}
            {showCreateModal && (
                <ModalPortal>
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500/75 p-4">
                        <div className="w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg bg-white text-left shadow-xl">
                            <form
                                onSubmit={handleCreateUser}
                                onChangeCapture={() => {
                                    setError(null);
                                    setSuccess(null);
                                }}
                            >
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                                        Add New User
                                    </h3>

                                    {error && showCreateModal && (
                                        <div className="mb-4 flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                            <span className="flex-1">{error}</span>
                                            <button
                                                type="button"
                                                className="text-red-600 hover:text-red-800"
                                                onClick={() => setError(null)}
                                                aria-label="Dismiss error"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}

                                    <div className="space-y-4">
                                        <div>
                                            <label className="form-label">Full Name</label>
                                            <input
                                                type="text"
                                                required
                                                className="form-input"
                                                value={formData.full_name}
                                                onChange={(e) => {
                                                    setFormData(prev => ({ ...prev, full_name: e.target.value }));
                                                    setError(null);
                                                    setFormErrors((prev) => ({ ...prev, full_name: '' }));
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <label className="form-label">Email</label>
                                            <input
                                                type="email"
                                                required
                                                className="form-input"
                                                value={formData.email}
                                                onChange={(e) => {
                                                    setFormData(prev => ({ ...prev, email: e.target.value }));
                                                    setFormErrors(prev => ({ ...prev, email: '' }));
                                                    setError(null);
                                                }}
                                            />
                                        </div>
                                        {formErrors.email && <p className="mt-1 text-xs text-red-600">{formErrors.email}</p>}

                                        {/* <div>
                                            <label className="form-label">Username</label>
                                            <input
                                                type="text"
                                                required
                                                className="form-input"
                                                value={formData.username}
                                                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                                            />
                                        </div> */}

                                        <div>
                                            <label className="form-label">Role</label>
                                            <select
                                                required
                                                className="form-input"
                                                value={formData.role}
                                                onChange={(e) => {
                                                    const nextRole = e.target.value;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        role: nextRole,
                                                        mp_id: MP_INDEPENDENT_ROLES.has(nextRole) ? undefined : prev.mp_id,
                                                    }));
                                                    setError(null);
                                                }}
                                            >
                                                <option value="">Select Role</option> {/* Added "Select Role" option */}
                                                {rolesForCreateDropdown.map(role => (
                                                    <option key={role} value={role}>
                                                        {formatRoleForDisplay(role)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {shouldShowMpSelectForRole(formData.role) && (
                                            <div>
                                                <label className="form-label">Mobilisation Partner</label>
                                                <select
                                                    required
                                                    className="form-input"
                                                    value={formData.mp_id || ''}
                                                    onChange={e => {
                                                        setFormData(prev => ({ ...prev, mp_id: Number(e.target.value) }));
                                                        setError(null);
                                                    }}
                                                >
                                                    <option value="">Select MP</option>
                                                    {mps.map(mp => (
                                                        <option key={mp.mp_id} value={mp.mp_id}>
                                                            {mp.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        <div>
                                            <label className="form-label">Phone</label>
                                            <input
                                                type="tel"
                                                required
                                                className="form-input"
                                                value={formData.phone}
                                                onChange={(e) => {
                                                    setFormData(prev => ({ ...prev, phone: e.target.value }));
                                                    setFormErrors(prev => ({ ...prev, phone: '' }));
                                                    setError(null);
                                                }}
                                            />
                                        </div>
                                        {formErrors.phone && <p className="mt-1 text-xs text-red-600">{formErrors.phone}</p>}

                                    </div>
                                </div>

                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button
                                        type="submit"
                                        className="btn-primary ml-3 disabled:cursor-not-allowed disabled:opacity-70"
                                        disabled={isSubmitting}
                                    >
                                        <span className="flex items-center gap-2">
                                            {isSubmitting && showCreateModal && (
                                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                                            )}
                                            {isSubmitting && showCreateModal ? 'Creating...' : 'Create User'}
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        onClick={() => {
                                            setShowCreateModal(false);
                                            resetForm();
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </ModalPortal>
            )}

            {/* Edit User Modal */}
            {showEditModal && editingUser && (
                <ModalPortal>
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500/75 p-4">
                        <div className="w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg bg-white text-left shadow-xl">
                            <form
                                onSubmit={handleEditUser}
                                onChangeCapture={() => {
                                    setError(null);
                                    setSuccess(null);
                                }}
                            >
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                                        Edit User: {editingUser.full_name}
                                    </h3>

                                    {error && showEditModal && (
                                        <div className="mb-4 flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                            <span className="flex-1">{error}</span>
                                            <button
                                                type="button"
                                                className="text-red-600 hover:text-red-800"
                                                onClick={() => setError(null)}
                                                aria-label="Dismiss error"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}

                                    <div className="space-y-4">
                                        <div>
                                            <label className="form-label">Full Name</label>
                                            <input
                                                type="text"
                                                required
                                                className="form-input"
                                                value={formData.full_name}
                                                onChange={(e) => {
                                                    setFormData(prev => ({ ...prev, full_name: e.target.value }));
                                                    setError(null);
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <label className="form-label">Email</label>
                                            <input
                                                type="email"
                                                required
                                                className="form-input"
                                                value={formData.email}
                                                onChange={(e) => {
                                                    setFormData(prev => ({ ...prev, email: e.target.value }));
                                                    setFormErrors(prev => ({ ...prev, email: '' }));
                                                    setError(null);
                                                }}
                                            />
                                        </div>
                                        {formErrors.email && <p className="mt-1 text-xs text-red-600">{formErrors.email}</p>}

                                        {/* <div>
                                            <label className="form-label">Username</label>
                                            <input
                                                type="text"
                                                required
                                                className="form-input"
                                                value={formData.username}
                                                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                                            />
                                        </div> */}

                                        <div>
                                            <label className="form-label">Role</label>
                                            <select
                                                required
                                                className="form-input"
                                                value={formData.role}
                                                onChange={(e) => {
                                                    const nextRole = e.target.value;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        role: nextRole,
                                                        mp_id: MP_INDEPENDENT_ROLES.has(nextRole) ? undefined : prev.mp_id,
                                                    }));
                                                    setError(null);
                                                }}
                                            >
                                                {rolesForCreateDropdown.map(role => (
                                                    <option key={role} value={role}>
                                                        {formatRoleForDisplay(role)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Mobilisation Partner selection for SYSTEM_ADMIN / SUPER_ADMIN */}
                                        {shouldShowMpSelectForRole(formData.role) && (
                                            <div>
                                                <label className="form-label">Mobilisation Partner</label>
                                                <select
                                                    required
                                                    className="form-input"
                                                    value={formData.mp_id || ''}
                                                    onChange={e => {
                                                        setFormData(prev => ({ ...prev, mp_id: Number(e.target.value) }));
                                                        setError(null);
                                                    }}
                                                >
                                                    <option value="">Select MP</option>
                                                    {mps.map(mp => (
                                                        <option key={mp.mp_id} value={mp.mp_id}>
                                                            {mp.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        <div>
                                            <label className="form-label">Phone</label>
                                            <input
                                                type="tel"
                                                required
                                                className="form-input"
                                                value={formData.phone}
                                                onChange={(e) => {
                                                    setFormData(prev => ({ ...prev, phone: e.target.value }));
                                                    setFormErrors(prev => ({ ...prev, phone: '' }));
                                                    setError(null);
                                                }}
                                            />
                                        </div>
                                        {formErrors.phone && <p className="mt-1 text-xs text-red-600">{formErrors.phone}</p>}

                                    </div>
                                </div>

                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button
                                        type="submit"
                                        className="btn-primary ml-3 disabled:cursor-not-allowed disabled:opacity-70"
                                        disabled={isSubmitting}
                                    >
                                        <span className="flex items-center gap-2">
                                            {isSubmitting && showEditModal && (
                                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                                            )}
                                            {isSubmitting && showEditModal ? 'Updating...' : 'Update User'}
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        onClick={() => {
                                            setShowEditModal(false);
                                            setEditingUser(null);
                                            resetForm();
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </ModalPortal>
            )}

            {/* ==== View User Modal ==== */}
            {showViewModal && viewingUser && (
                <ModalPortal>
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">User Details</h2>
                            <button
                                onClick={() => setShowViewModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                            <div>
                                <p className="text-sm text-gray-500">Full Name</p>
                                <p className="text-gray-900">{viewingUser.full_name}</p>
                            </div>
                            {/* <div>
                                <p className="text-sm text-gray-500">Username</p>
                                <p className="text-gray-900">{viewingUser.username}</p>
                            </div> */}
                            <div>
                                <p className="text-sm text-gray-500">Email</p>
                                <p className="text-gray-900">{viewingUser.email}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Phone</p>
                                <p className="text-gray-900">{viewingUser.phone || '—'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Role</p>
                                <p className="text-gray-900">{viewingUser.role}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Status</p>
                                <p className="text-gray-900">{viewingUser.is_active ? 'Active' : 'Inactive'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Mobilisation Partner</p>
                                <p className="text-gray-900">{viewingUser.mobilisation_partner?.name ?? '—'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Last Login</p>
                                <p className="text-gray-900">
                                    {viewingUser.last_login
                                        ? new Date(viewingUser.last_login).toLocaleString()
                                        : 'Never'}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Created At</p>
                                <p className="text-gray-900">
                                    {new Date(viewingUser.created_at).toLocaleString()}
                                </p>
                            </div>
                            {/* <div>
                <p className="text-sm text-gray-500">Metadata</p>
                <pre className="text-xs text-gray-700 bg-gray-100 p-2 rounded overflow-auto">
                  {JSON.stringify(viewingUser.metadata, null, 2)}
                </pre>
              </div> */}
                            {/* add any other fields you need */}
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setShowViewModal(false)}
                                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                            >
                                Close
                            </button>
                        </div>
                        </div>
                    </div>
                </ModalPortal>
            )}
        </div>
    );
};

export default UserManagement;







































